import React, { useState, useEffect, useMemo } from 'react';
import { 
  Modal,
  Form,
  Alert,
  OverlayTrigger,
  Tooltip,
  Card,
  Badge,
  Button
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { 
  People as Users, 
  PersonAdd as UserPlus, 
  Shield, 
  VpnKey as Key, 
  Lock, 
  LockOpen as Unlock, 
  Search, 
  AccessTime as Clock, 
  Timeline as Activity, 
  Description as FileText, 
  Visibility as Eye, 
  LocalOffer as Tag,
  CheckCircle,
  Cancel as NoLogin
} from '@mui/icons-material';
import { useAuth } from './AuthContext';
import userApi from '../../api/userApi';
import { loadAllActors, loadAllUsers, loadActorTags, buildPeopleList } from './userManagementActors';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Alert as MuiAlert,
  Box,
  Button as MuiButton
} from '@mui/material';
import './UserManagement.css';

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [actors, setActors] = useState([]);
  const [actorTags, setActorTags] = useState({}); // Map of actor_id -> tags
  const [actorsLoading, setActorsLoading] = useState(true);
  const [actorsError, setActorsError] = useState(null);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('people');
  
  // Search for people tab
  const [searchTerm, setSearchTerm] = useState('');
  const [showAllContacts, setShowAllContacts] = useState(false);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetPasswordSaving, setResetPasswordSaving] = useState(false);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState('');
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showGrantAccessModal, setShowGrantAccessModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedActor, setSelectedActor] = useState(null);
  const [selectedUserPermissions, setSelectedUserPermissions] = useState([]);
  
  // Form states
  const [newUserForm, setNewUserForm] = useState({
    email: '',
    password: '',
    actor_id: '',
    is_admin: false
  });
  const [resetPasswordForm, setResetPasswordForm] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [grantAccessForm, setGrantAccessForm] = useState({
    password: '',
    confirmPassword: '',
    isAdmin: false
  });
  const [formError, setFormError] = useState('');
  
  // Audit logs
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);
  
  // Login attempts
  const [loginAttempts, setLoginAttempts] = useState([]);
  const [loadingLoginAttempts, setLoadingLoginAttempts] = useState(false);
  const [loginSearch, setLoginSearch] = useState('');           // free-text search box
  const [loginFilterEmail, setLoginFilterEmail] = useState(''); // per-user drill-down
  
  // Pagination for audit logs & login attempts (server-side)
  const [auditLogsPage, setAuditLogsPage] = useState(1);
  const [loginAttemptsPage, setLoginAttemptsPage] = useState(1);
  const [totalAuditLogs, setTotalAuditLogs] = useState(0);
  const [totalLoginAttempts, setTotalLoginAttempts] = useState(0);
  const TABLE_PAGE_SIZE = 5;
  
  // Load users on component mount
  useEffect(() => {
    fetchUsers();
    fetchActors();
  }, []);
  
  // Fetch users
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    setUsersLoaded(false);
    
    try {
      const data = await loadAllUsers();
      setUsers(data);
      setUsersLoaded(true);
    } catch (err) {
      setError(err.message || 'Failed to load users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch actors
  const fetchActors = async () => {
    setActorsLoading(true);
    setActorsError(null);
    setActorTags({});
    try {
      const data = await loadAllActors();
      setActors(data);
      setActorTags(await loadActorTags(data));
    } catch (err) {
      console.error('Error fetching actors:', err);
      setActorsError('Unable to load actors and their tags. Reload the page to try again.');
    } finally {
      setActorsLoading(false);
    }
  };

  // A contact without a staff tag is normally hidden, but a search must still
  // find the contact somebody created a minute ago, so searching includes them.
  const includeUntagged = showAllContacts || searchTerm.trim().length > 0;
  const peopleList = useMemo(() => buildPeopleList(
    actors, users, usersLoaded && !actorsError ? actorTags : {}, { includeUntagged }
  ), [actors, users, actorTags, usersLoaded, actorsError, includeUntagged]);
  const availableActors = useMemo(() => peopleList
    .filter(person => !person.hasLogin)
    .map(person => person.actor), [peopleList]);

  // Filtered people (search only)
  const filteredPeople = useMemo(() => {
    let list = [...peopleList];
    
    // Apply search
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(p => {
        const name = `${p.actor.first_name || ''} ${p.actor.last_name || ''} ${p.actor.name || ''}`.toLowerCase();
        const email = (p.actor.email || '').toLowerCase();
        const userEmail = (p.user?.email || '').toLowerCase();
        return name.includes(q) || email.includes(q) || userEmail.includes(q);
      });
    }
    
    // Sort alphabetically
    list.sort((a, b) => {
      const nameA = `${a.actor.first_name || ''} ${a.actor.last_name || ''} ${a.actor.name || ''}`.trim().toLowerCase();
      const nameB = `${b.actor.first_name || ''} ${b.actor.last_name || ''} ${b.actor.name || ''}`.trim().toLowerCase();
      return nameA.localeCompare(nameB);
    });
    
    return list;
  }, [peopleList, searchTerm]);
  
  // Fetch audit logs with server-side pagination
  const fetchAuditLogs = async (page) => {
    setLoadingAuditLogs(true);
    const p = page || auditLogsPage;
    const skip = (p - 1) * TABLE_PAGE_SIZE;
    
    try {
      const data = await userApi.getAuditLogs({ skip, limit: TABLE_PAGE_SIZE });
      if (data && data.items) {
        setAuditLogs(data.items);
        setTotalAuditLogs(data.total || 0);
      } else {
        const arr = Array.isArray(data) ? data : [];
        setAuditLogs(arr);
        setTotalAuditLogs(arr.length);
      }
    } catch (err) {
      console.error('Error fetching audit logs:', err);
    } finally {
      setLoadingAuditLogs(false);
    }
  };
  
  // Fetch login attempts with server-side pagination
  const fetchLoginAttempts = async (page, overrides = {}) => {
    setLoadingLoginAttempts(true);
    const p = page || loginAttemptsPage;
    const skip = (p - 1) * TABLE_PAGE_SIZE;
    const searchTerm = overrides.search !== undefined ? overrides.search : loginSearch;
    const emailFilter = overrides.email !== undefined ? overrides.email : loginFilterEmail;

    try {
      const data = await userApi.getLoginAttempts({
        skip, limit: TABLE_PAGE_SIZE,
        ...(searchTerm ? { search: searchTerm } : {}),
        ...(emailFilter ? { email: emailFilter } : {}),
      });
      if (data && data.items) {
        setLoginAttempts(data.items);
        setTotalLoginAttempts(data.total || 0);
      } else {
        const arr = Array.isArray(data) ? data : [];
        setLoginAttempts(arr);
        setTotalLoginAttempts(arr.length);
      }
    } catch (err) {
      console.error('Error fetching login attempts:', err);
    } finally {
      setLoadingLoginAttempts(false);
    }
  };
  
  // Handle tab change
  const handleTabSelect = (key) => {
    setActiveTab(key);
    
    if (key === 'auditLogs') {
      setAuditLogsPage(1);
      fetchAuditLogs(1);
    } else if (key === 'loginAttempts') {
      setLoginAttemptsPage(1);
      fetchLoginAttempts(1);
    }
  };
  
  // Re-fetch when page changes
  const handleAuditLogsPageChange = (newPage) => {
    setAuditLogsPage(newPage);
    fetchAuditLogs(newPage);
  };
  
  const handleLoginAttemptsPageChange = (newPage) => {
    setLoginAttemptsPage(newPage);
    fetchLoginAttempts(newPage);
  };

  const handleLoginSearch = (value) => {
    setLoginSearch(value);
    setLoginAttemptsPage(1);
    fetchLoginAttempts(1, { search: value });
  };

  // Drill-down: click an email to see just that user's full login history.
  const showUserLoginAttempts = (email) => {
    if (!email) return;
    setLoginFilterEmail(email);
    setLoginSearch('');
    setLoginAttemptsPage(1);
    fetchLoginAttempts(1, { email, search: '' });
  };

  const clearLoginFilters = () => {
    setLoginFilterEmail('');
    setLoginSearch('');
    setLoginAttemptsPage(1);
    fetchLoginAttempts(1, { email: '', search: '' });
  };
  
  // Handle ban/unban user
  const handleBanUser = async (userId, isBanned) => {
    try {
      if (isBanned) {
        await userApi.unbanUser(userId);
      } else {
        await userApi.banUser(userId);
      }
      
      // Refresh user list
      fetchUsers();
    } catch (err) {
      setError(err.message || `Failed to ${isBanned ? 'unban' : 'ban'} user`);
      console.error('Error updating user status:', err);
    }
  };
  
  // Handle reset password modal
  const handleResetPassword = (user) => {
    setResetPasswordSuccess('');
    setSelectedUser(user);
    setResetPasswordForm({
      new_password: '',
      confirm_password: ''
    });
    setFormError('');
    setShowResetModal(true);
  };
  
  // Handle create user modal
  const handleCreateUserClick = () => {
    setNewUserForm({
      email: '',
      password: '',
      actor_id: '',
      is_admin: false
    });
    setFormError('');
    setShowCreateModal(true);
  };
  
  // Handle new user form change
  const handleNewUserFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewUserForm({
      ...newUserForm,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Handle reset password form change
  const handleResetPasswordFormChange = (e) => {
    const { name, value } = e.target;
    setResetPasswordForm({
      ...resetPasswordForm,
      [name]: value
    });
  };
  
  // Handle create user submit
  const handleCreateUserSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    
    // Validate form
    if (!newUserForm.email || !newUserForm.password || !newUserForm.actor_id) {
      setFormError('All fields are required');
      return;
    }
    
    if (newUserForm.password.length < 8) {
      setFormError('Password must be at least 8 characters long');
      return;
    }
    if (!availableActors.some(actor => actor.id === newUserForm.actor_id)) {
      setFormError('Select an actor with a tag other than Customer and no existing login');
      return;
    }
    
    try {
      await userApi.createUser(newUserForm);
      setShowCreateModal(false);
      fetchUsers(); // Refresh user list
    } catch (err) {
      setFormError(err.message || 'Failed to create user');
      console.error('Error creating user:', err);
    }
  };
  
  // Handle reset password submit
  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    if (resetPasswordSaving) return;
    setFormError('');
    
    // Validate form
    if (!resetPasswordForm.new_password) {
      setFormError('Password is required');
      return;
    }
    
    if (resetPasswordForm.new_password.length < 8) {
      setFormError('Password must be at least 8 characters long');
      return;
    }
    
    if (resetPasswordForm.new_password !== resetPasswordForm.confirm_password) {
      setFormError('Passwords do not match');
      return;
    }
    
    setResetPasswordSaving(true);
    try {
      await userApi.resetPassword(selectedUser.id, resetPasswordForm.new_password);
      setResetPasswordForm({ new_password: '', confirm_password: '' });
      setShowResetModal(false);
      setResetPasswordSuccess('Password reset successfully. The user must change it on next login.');
      fetchUsers(); // Refresh user list
    } catch (err) {
      setFormError(`Password was not reset. ${err.message || 'Please try again.'}`);
    } finally {
      setResetPasswordSaving(false);
    }
  };
  
  // Handle view permissions - shows all permissions user gets from actor's tags
  const handleViewPermissions = (user) => {
    setSelectedUser(user);
    
    // Get tags for this user's actor
    const userActorTags = actorTags[user.actor_id] || [];
    
    // Collect all unique permissions from all tags
    const allPermissions = new Set();
    userActorTags.forEach(tag => {
      (tag.permissions || []).forEach(perm => allPermissions.add(perm));
    });
    
    setSelectedUserPermissions(Array.from(allPermissions));
    setShowPermissionsModal(true);
  };

  // Get actor tags for a user
  const getUserActorTags = (actorId) => {
    return actorTags[actorId] || [];
  };
  
  // Find actor display name by ID - show actor name or user roles
  const getActorName = (actorId, userObj) => {
    const actor = actors.find(a => a.id === actorId);
    if (actor) {
      const name = `${actor.first_name || ''} ${actor.last_name || ''}`.trim();
      if (name) return name;
    }
    // Fallback: show roles if available
    if (userObj?.roles && userObj.roles.length > 0) {
      return userObj.roles.join(', ');
    }
    // Last fallback: extract name from email
    const emailPrefix = (userObj?.email || '').split('@')[0];
    return emailPrefix || 'User';
  };

  // Handle delete user
  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Are you sure you want to delete user "${user.email}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await userApi.deleteUser(user.id);
      fetchUsers();
    } catch (err) {
      setError(err.message || 'Failed to delete user');
      console.error('Error deleting user:', err);
    }
  };
  
  // Format timestamp
  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  // Handle grant access click
  const handleGrantAccessClick = (actor) => {
    setSelectedActor(actor);
    setGrantAccessForm({ password: '', confirmPassword: '', isAdmin: false });
    setFormError('');
    setShowGrantAccessModal(true);
  };

  // Handle grant access form change
  const handleGrantAccessFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setGrantAccessForm({
      ...grantAccessForm,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Handle grant access submit
  const handleGrantAccessSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!availableActors.some(actor => actor.id === selectedActor?.id)) {
      setFormError('This actor is no longer available for login access');
      return;
    }

    if (!grantAccessForm.password) {
      setFormError('Password is required');
      return;
    }
    if (grantAccessForm.password.length < 8) {
      setFormError('Password must be at least 8 characters');
      return;
    }
    if (grantAccessForm.password !== grantAccessForm.confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    try {
      const newUser = await userApi.convertActorToUser(
        selectedActor.id,
        grantAccessForm.password,
        [],
        grantAccessForm.isAdmin
      );
      // Try to sync roles from tags
      if (newUser && newUser.id) {
        try {
          await userApi.syncUserRolesWithTags(newUser.id);
        } catch (syncErr) {
          console.warn('Role sync failed:', syncErr);
        }
      }
      setShowGrantAccessModal(false);
      fetchUsers();
    } catch (err) {
      setFormError(err.message || 'Failed to grant access');
    }
  };

  // Handle revoke access (delete user login but keep actor)
  const handleRevokeAccess = async (user) => {
    if (!window.confirm(`Remove login access for "${user.email}"? The person's data will remain, only login is removed.`)) {
      return;
    }
    try {
      await userApi.deleteUser(user.id);
      fetchUsers();
    } catch (err) {
      setError(err.message || 'Failed to revoke access');
    }
  };

  // Tab configuration - simplified: no more "Convert Actors"
  const tabs = [
    { id: 'people', label: 'People', icon: Users, color: '#3b82f6', gradient: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)' },
    { id: 'auditLogs', label: 'Audit Logs', icon: FileText, color: '#10b981', gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' },
    { id: 'loginAttempts', label: 'Login Attempts', icon: Activity, color: '#f59e0b', gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }
  ];

  // Get initials for avatar
  const getInitials = (name, email) => {
    if (name && name.trim()) return name.trim().charAt(0).toUpperCase();
    return email ? email.charAt(0).toUpperCase() : '?';
  };

  // Get status class
  const getStatusClass = (status) => {
    if (status === 'active') return 'active';
    if (status === 'banned') return 'banned';
    if (status === 'pending_password_change') return 'pending';
    return 'inactive';
  };

  // Get actor display name
  const getPersonName = (actor) => {
    const name = `${actor.first_name || ''} ${actor.last_name || ''}`.trim();
    return name || actor.name || actor.email || 'Unknown';
  };

  return (
    <div className="user-management-modern">
      {/* Hero Header */}
      <div className="um-hero">
        <div className="um-hero-content">
          <div className="um-hero-icon">
            <Users size={32} />
          </div>
          <div className="um-hero-text">
            <h1>User Management</h1>
            <p>Manage system users, permissions, and access control</p>
          </div>
        </div>
        <div className="um-hero-stats">
          <div className="um-stat-item">
            <Users size={20} />
            <span className="um-stat-value">{peopleList.length}</span>
            <span className="um-stat-label">People</span>
          </div>
          <div className="um-stat-item">
            <CheckCircle size={20} />
            <span className="um-stat-value">{users.length}</span>
            <span className="um-stat-label">With Login</span>
          </div>
          <div className="um-stat-item">
            <Shield size={20} />
            <span className="um-stat-value">{users.filter(u => u.is_admin).length}</span>
            <span className="um-stat-label">Admins</span>
          </div>
          <div className="um-stat-item">
            <Activity size={20} />
            <span className="um-stat-value">{users.filter(u => u.status === 'active').length}</span>
            <span className="um-stat-label">Active</span>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {resetPasswordSuccess && (
        <Alert variant="success" onClose={() => setResetPasswordSuccess('')} dismissible style={{ margin: '1rem 2rem 0', borderRadius: '12px' }}>
          {resetPasswordSuccess}
        </Alert>
      )}
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible style={{ margin: '1rem 2rem 0', borderRadius: '12px' }}>
          {error}
        </Alert>
      )}
      {actorsError && (
        <Alert variant="danger" style={{ margin: '1rem 2rem 0', borderRadius: '12px' }}>
          {actorsError}
        </Alert>
      )}

      {/* Tab Navigation */}
      <div className="um-tabs-container">
        <div className="um-tabs">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                className={`um-tab ${isActive ? 'active' : ''}`}
                onClick={() => handleTabSelect(tab.id)}
                style={isActive ? { '--tab-color': tab.color, '--tab-gradient': tab.gradient } : {}}
              >
                <div className="um-tab-icon" style={isActive ? { background: tab.gradient } : {}}>
                  <Icon size={20} />
                </div>
                <span className="um-tab-label">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="um-content">
        {activeTab === 'people' && (
          <>
            {/* Search bar */}
            <div className="um-header-bar">
              <div className="um-search">
                <Search size={18} className="um-search-icon" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <label className="um-toggle" title="Contacts without a staff tag are hidden until you search for them">
                <input type="checkbox" checked={showAllContacts}
                  onChange={(e) => setShowAllContacts(e.target.checked)} />
                <span>Show every contact</span>
              </label>
              <button className="um-create-btn" onClick={handleCreateUserClick} disabled={loading || actorsLoading || !usersLoaded || !!actorsError}>
                <UserPlus size={20} />
                Create User
              </button>
            </div>

            {loading || actorsLoading ? (
              <div className="um-loading">
                <div className="um-spinner"></div>
                <span>Loading...</span>
              </div>
            ) : (
              <>
                {/* SECTION 1: Users with Login Access */}
                <div style={{ marginBottom: 32 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: '0 4px' }}>
                    <CheckCircle style={{ color: '#16a34a', fontSize: 22 }} />
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                      Users with Login Access ({peopleList.filter(p => p.hasLogin).length})
                    </h3>
                  </div>
                  
                  {peopleList.filter(p => p.hasLogin).length === 0 ? (
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '24px', textAlign: 'center', color: '#64748b' }}>
                      No users with login access yet
                    </div>
                  ) : (
                    <div className="um-table-container">
                      <table className="um-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Login Email</th>
                            <th>Role</th>
                            <th>Status</th>
                            <th>Last Login</th>
                            <th>Tags</th>
                            <th style={{ textAlign: 'right' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredPeople
                            .filter(p => p.hasLogin)
                            .map(({ actor, user: userAccount }) => {
                              const tags = getUserActorTags(actor.id);
                              const personName = getPersonName(actor);
                              return (
                                <tr key={userAccount.id}>
                                  <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                      <div style={{
                                        width: 34, height: 34, borderRadius: '50%', display: 'flex',
                                        alignItems: 'center', justifyContent: 'center', fontWeight: 700,
                                        fontSize: 13, color: '#fff',
                                        background: userAccount?.is_admin ? 'linear-gradient(135deg, #f59e0b, #d97706)' : '#16a34a'
                                      }}>
                                        {getInitials(personName, actor.email)}
                                      </div>
                                      <span style={{ fontWeight: 600, fontSize: 14 }}>{personName}</span>
                                    </div>
                                  </td>
                                  <td style={{ fontSize: 13, color: '#475569' }}>{userAccount?.email}</td>
                                  <td>
                                    {userAccount?.is_admin ? (
                                      <span style={{ background: '#fef3c7', color: '#92400e', padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>Admin</span>
                                    ) : (
                                      <span style={{ background: '#e0e7ff', color: '#3730a3', padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>User</span>
                                    )}
                                  </td>
                                  <td>
                                    <span className={`um-status ${getStatusClass(userAccount?.status)}`}>
                                      {userAccount?.status?.replace('_', ' ') || 'unknown'}
                                    </span>
                                  </td>
                                  <td style={{ fontSize: 12, color: '#64748b' }}>
                                    {userAccount?.last_login ? formatDateTime(userAccount.last_login) : 'Never'}
                                  </td>
                                  <td>
                                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                      {tags.length > 0 ? tags.map(tag => (
                                        <span key={tag.id} style={{
                                          backgroundColor: tag.color || '#6c757d', color: '#fff',
                                          padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600
                                        }}>
                                          {tag.name}
                                        </span>
                                      )) : <span style={{ fontSize: 12, color: '#94a3b8' }}>—</span>}
                                    </div>
                                  </td>
                                  <td>
                                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                                      {userAccount?.id !== currentUser?.id && (
                                        <>
                                          <button className="um-action-btn permissions" onClick={() => handleViewPermissions(userAccount)} title="View Permissions">
                                            <Eye size={15} />
                                          </button>
                                          <button className="um-action-btn reset" onClick={() => handleResetPassword(userAccount)} title="Reset Password">
                                            <Key size={15} />
                                          </button>
                                          {userAccount?.status === 'banned' ? (
                                            <button className="um-action-btn unban" onClick={() => handleBanUser(userAccount.id, true)} title="Unban">
                                              <Unlock size={15} />
                                            </button>
                                          ) : (
                                            <button className="um-action-btn ban" onClick={() => handleBanUser(userAccount.id, false)} title="Ban">
                                              <Lock size={15} />
                                            </button>
                                          )}
                                          <button className="um-action-btn delete" onClick={() => handleRevokeAccess(userAccount)} title="Remove Login" style={{ color: '#dc3545' }}>
                                            <NoLogin size={15} />
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* SECTION 2: Actors without Login */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: '0 4px' }}>
                    <UserPlus style={{ color: '#64748b', fontSize: 22 }} />
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                      Actors without Login ({peopleList.filter(p => !p.hasLogin).length})
                    </h3>
                    <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 8 }}>Click &quot;Grant Login&quot; to give an actor system access</span>
                  </div>

                  {filteredPeople.filter(p => !p.hasLogin).length === 0 ? (
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '24px', textAlign: 'center', color: '#64748b' }}>
                      {searchTerm ? 'No actors match your search' : 'No actors with a tag other than Customer are available without login'}
                    </div>
                  ) : (
                    <div className="um-table-container">
                      <table className="um-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Type</th>
                            <th>Tags</th>
                            <th style={{ textAlign: 'right' }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredPeople
                            .filter(p => !p.hasLogin)
                            .map(({ actor }) => {
                              const tags = getUserActorTags(actor.id);
                              const personName = getPersonName(actor);
                              return (
                                <tr key={actor.id}>
                                  <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                      <div style={{
                                        width: 34, height: 34, borderRadius: '50%', display: 'flex',
                                        alignItems: 'center', justifyContent: 'center', fontWeight: 700,
                                        fontSize: 13, color: '#fff', background: '#94a3b8'
                                      }}>
                                        {getInitials(personName, actor.email)}
                                      </div>
                                      <span style={{ fontWeight: 600, fontSize: 14 }}>{personName}</span>
                                    </div>
                                  </td>
                                  <td style={{ fontSize: 13, color: '#475569' }}>{actor.email || '—'}</td>
                                  <td>
                                    <span style={{ background: '#f1f5f9', color: '#475569', padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                                      {actor.type || 'person'}
                                    </span>
                                  </td>
                                  <td>
                                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                      {tags.length > 0 ? tags.map(tag => (
                                        <span key={tag.id} style={{
                                          backgroundColor: tag.color || '#6c757d', color: '#fff',
                                          padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600
                                        }}>
                                          {tag.name}
                                        </span>
                                      )) : <span style={{ fontSize: 12, color: '#94a3b8' }}>—</span>}
                                    </div>
                                  </td>
                                  <td style={{ textAlign: 'right' }}>
                                    <button
                                      onClick={() => handleGrantAccessClick(actor)}
                                      style={{
                                        padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 700,
                                        background: '#16a34a', color: '#fff', border: 'none', cursor: 'pointer',
                                        display: 'inline-flex', alignItems: 'center', gap: 5,
                                      }}
                                    >
                                      <Key style={{ fontSize: 14 }} />
                                      Grant Login
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {activeTab === 'auditLogs' && (
          <>
            {loadingAuditLogs ? (
              <div className="um-loading">
                <div className="um-spinner"></div>
                <span>Loading audit logs...</span>
              </div>
            ) : auditLogs.length === 0 ? (
              <div className="um-empty">
                <div className="um-empty-icon">
                  <FileText size={36} />
                </div>
                <h3>No Audit Logs</h3>
                <p>System activity will appear here</p>
              </div>
            ) : (
              <div className="um-table-container">
                <table className="um-table">
                  <thead>
                    <tr>
                      <th>Timestamp</th>
                      <th>User</th>
                      <th>Action</th>
                      <th>Details</th>
                      <th>IP Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map(log => (
                      <tr key={log.id}>
                        <td>{formatDateTime(log.timestamp)}</td>
                        <td>{log.email}</td>
                        <td>
                          <span className={`um-action-badge ${log.action_type.includes('login') ? 'login' : 'update'}`}>
                            {log.action_type.replace('_', ' ')}
                          </span>
                        </td>
                        <td>{log.details || 'No details'}</td>
                        <td>{log.ip_address || 'Unknown'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Audit Logs Pagination */}
                {Math.ceil(totalAuditLogs / TABLE_PAGE_SIZE) > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-3 px-2">
                    <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                      Showing {((auditLogsPage - 1) * TABLE_PAGE_SIZE) + 1}{"\u2013"}{Math.min(auditLogsPage * TABLE_PAGE_SIZE, totalAuditLogs)} of {totalAuditLogs}
                    </span>
                    <div className="d-flex gap-1">
                      <button className="btn btn-sm btn-outline-secondary" disabled={auditLogsPage === 1} onClick={() => handleAuditLogsPageChange(auditLogsPage - 1)}>{"\u2039"} Prev</button>
                      <span className="btn btn-sm btn-outline-secondary disabled">{auditLogsPage} / {Math.ceil(totalAuditLogs / TABLE_PAGE_SIZE)}</span>
                      <button className="btn btn-sm btn-outline-secondary" disabled={auditLogsPage >= Math.ceil(totalAuditLogs / TABLE_PAGE_SIZE)} onClick={() => handleAuditLogsPageChange(auditLogsPage + 1)}>Next {"\u203a"}</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {activeTab === 'loginAttempts' && (
          <>
            <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ maxWidth: 320 }}
                placeholder="Search email, IP or location…"
                value={loginSearch}
                onChange={(e) => handleLoginSearch(e.target.value)}
              />
              {loginFilterEmail && (
                <span className="um-action-badge login" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  Showing: {loginFilterEmail}
                  <button
                    type="button"
                    onClick={clearLoginFilters}
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 700, lineHeight: 1 }}
                    aria-label="Clear user filter"
                  >×</button>
                </span>
              )}
            </div>
            {loadingLoginAttempts ? (
              <div className="um-loading">
                <div className="um-spinner"></div>
                <span>Loading login attempts...</span>
              </div>
            ) : loginAttempts.length === 0 ? (
              <div className="um-empty">
                <div className="um-empty-icon">
                  <Activity size={36} />
                </div>
                <h3>No Login Attempts</h3>
                <p>Login activity will appear here</p>
              </div>
            ) : (
              <div className="um-table-container">
                <table className="um-table">
                  <thead>
                    <tr>
                      <th>Timestamp</th>
                      <th>Email</th>
                      <th>Result</th>
                      <th>IP Address</th>
                      <th>Location</th>
                      <th>User Agent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loginAttempts.map(attempt => (
                      <tr key={attempt.id}>
                        <td>{formatDateTime(attempt.timestamp)}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            style={{ textDecoration: 'none', fontWeight: 600 }}
                            title="Show this user's login history"
                            onClick={() => showUserLoginAttempts(attempt.email)}
                          >
                            {attempt.email}
                          </button>
                        </td>
                        <td>
                          <span className={`um-action-badge ${attempt.result === 'success' ? 'login' : 'failed'}`}>
                            {attempt.result}
                          </span>
                        </td>
                        <td>{attempt.ip_address || 'Unknown'}</td>
                        <td>{attempt.location || '—'}</td>
                        <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {attempt.user_agent || 'Unknown'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Login Attempts Pagination */}
                {Math.ceil(totalLoginAttempts / TABLE_PAGE_SIZE) > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-3 px-2">
                    <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                      Showing {((loginAttemptsPage - 1) * TABLE_PAGE_SIZE) + 1}{"\u2013"}{Math.min(loginAttemptsPage * TABLE_PAGE_SIZE, totalLoginAttempts)} of {totalLoginAttempts}
                    </span>
                    <div className="d-flex gap-1">
                      <button className="btn btn-sm btn-outline-secondary" disabled={loginAttemptsPage === 1} onClick={() => handleLoginAttemptsPageChange(loginAttemptsPage - 1)}>{"\u2039"} Prev</button>
                      <span className="btn btn-sm btn-outline-secondary disabled">{loginAttemptsPage} / {Math.ceil(totalLoginAttempts / TABLE_PAGE_SIZE)}</span>
                      <button className="btn btn-sm btn-outline-secondary" disabled={loginAttemptsPage >= Math.ceil(totalLoginAttempts / TABLE_PAGE_SIZE)} onClick={() => handleLoginAttemptsPageChange(loginAttemptsPage + 1)}>Next {"\u203a"}</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Create User Modal */}
      <Dialog
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '16px',
            padding: '8px'
          }
        }}
      >
        <form onSubmit={handleCreateUserSubmit} style={{ width: '100%' }}>
          <DialogTitle style={{ fontWeight: 700, fontSize: '1.25rem' }}>
            Create New User
          </DialogTitle>
          <DialogContent dividers>
            {formError && (
              <MuiAlert severity="error" sx={{ mb: 2 }}>{formError}</MuiAlert>
            )}
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                type="email"
                autoComplete="email"
                value={newUserForm.email}
                onChange={handleNewUserFormChange}
                variant="outlined"
              />
              
              <TextField
                required
                fullWidth
                name="password"
                label="Initial Password"
                type="password"
                id="password"
                value={newUserForm.password}
                onChange={handleNewUserFormChange}
                variant="outlined"
                inputProps={{ minLength: 8 }}
                helperText="Must be at least 8 characters long. User will be prompted to change on first login."
              />
              
              <Autocomplete
                options={availableActors}
                getOptionLabel={(option) => {
                  const name = [option.first_name, option.last_name].filter(Boolean).join(' ') || option.name || '';
                  return name ? `${name} (${option.email || ''})` : (option.email || '');
                }}
                value={availableActors.find(a => a.id === newUserForm.actor_id) || null}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText="No actors with a tag other than Customer are available without login"
                onChange={(event, newValue) => {
                  setNewUserForm(prev => ({ ...prev, actor_id: newValue ? newValue.id : '' }));
                }}
                renderOption={(props, option) => {
                  const name = [option.first_name, option.last_name].filter(Boolean).join(' ') || option.name || 'Unnamed';
                  return (
                    <Box component="li" {...props} key={option.id}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{name}</div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{option.email}</div>
                      </div>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Associated Actor"
                    required
                    placeholder="Search by name or email..."
                    helperText="Actors with at least one tag other than Customer and no existing login."
                  />
                )}
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newUserForm.is_admin}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, is_admin: e.target.checked }))}
                    color="primary"
                  />
                }
                label="Grant Administrator privileges"
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <MuiButton 
              onClick={() => setShowCreateModal(false)} 
              variant="outlined" 
              color="error"
              sx={{ borderRadius: '8px' }}
            >
              Cancel
            </MuiButton>
            <MuiButton 
              type="submit" 
              variant="contained" 
              color="primary"
              sx={{ 
                borderRadius: '8px',
                background: '#22c55e',
                '&:hover': {
                  background: '#16a34a'
                }
              }}
            >
              Create User
            </MuiButton>
          </DialogActions>
        </form>
      </Dialog>
          
          {/* Reset Password Modal */}
          <Modal
            show={showResetModal}
            onHide={() => !resetPasswordSaving && setShowResetModal(false)}
            backdrop="static"
            keyboard={false}
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>Reset Password</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {selectedUser && (
                <>
                  <Alert variant="info">
                    Resetting password for <strong>{selectedUser.email}</strong>.
                    The user will be required to change this password on next login.
                  </Alert>
                  
                  {formError && (
                    <Alert variant="danger">{formError}</Alert>
                  )}
                  
                  <Form onSubmit={handleResetPasswordSubmit}>
                    <Form.Group className="mb-3" controlId="newPassword">
                      <Form.Label>New Password</Form.Label>
                      <Form.Control
                        type="password"
                        name="new_password"
                        value={resetPasswordForm.new_password}
                        onChange={handleResetPasswordFormChange}
                        required
                        minLength={8}
                        autoComplete="new-password"
                        aria-describedby="resetPasswordRequirements"
                        disabled={resetPasswordSaving}
                      />
                      <Form.Text id="resetPasswordRequirements" className="text-muted">
                        Use at least 8 characters, uppercase and lowercase letters, a digit and a special character. The server checks the password policy before saving.
                      </Form.Text>
                    </Form.Group>
                    
                    <Form.Group className="mb-3" controlId="confirmPassword">
                      <Form.Label>Confirm New Password</Form.Label>
                      <Form.Control
                        type="password"
                        name="confirm_password"
                        value={resetPasswordForm.confirm_password}
                        onChange={handleResetPasswordFormChange}
                        required
                        autoComplete="new-password"
                        disabled={resetPasswordSaving}
                      />
                    </Form.Group>
                    
                    <div className="d-flex justify-content-end gap-2">
                      <Button variant="danger" disabled={resetPasswordSaving} onClick={() => setShowResetModal(false)}>
                        Cancel
                      </Button>
                      <Button variant="warning" type="submit" disabled={resetPasswordSaving}>
                        {resetPasswordSaving ? 'Resetting...' : 'Reset Password'}
                      </Button>
                    </div>
                  </Form>
                </>
              )}
            </Modal.Body>
          </Modal>
          
          {/* View Permissions Modal */}
          <Modal
            show={showPermissionsModal}
            onHide={() => setShowPermissionsModal(false)}
            size="lg"
          >
            <Modal.Header closeButton className="bg-info text-white">
              <Modal.Title>
                <i className="bi bi-shield-check me-2"></i>
                User Permissions
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {selectedUser && (
                <>
                  <Alert variant="info">
                    <i className="bi bi-info-circle me-2"></i>
                    Permissions for <strong>{selectedUser.email}</strong>
                    <br />
                    <small className="text-muted">
                      These permissions are inherited from the tags assigned to the linked actor.
                      To change permissions, edit the actor&apos;s tags in Actor Management.
                    </small>
                  </Alert>
                  
                  <Card className="mb-3">
                    <Card.Header className="bg-light">
                      <strong><i className="bi bi-person me-2"></i>Linked Actor</strong>
                    </Card.Header>
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center">
                        <span>{getActorName(selectedUser.actor_id, selectedUser)}</span>
                        <Link to="/actors" className="btn btn-sm btn-outline-primary">
                          <i className="bi bi-pencil me-1"></i>Edit Actor Tags
                        </Link>
                      </div>
                    </Card.Body>
                  </Card>
                  
                  <Card className="mb-3">
                    <Card.Header className="bg-light">
                      <strong><i className="bi bi-tags me-2"></i>Actor Tags</strong>
                    </Card.Header>
                    <Card.Body>
                      {(() => {
                        const tags = getUserActorTags(selectedUser.actor_id);
                        if (tags.length > 0) {
                          return (
                            <div className="d-flex flex-wrap gap-2">
                              {tags.map(tag => (
                                <Badge 
                                  key={tag.id}
                                  style={{
                                    backgroundColor: tag.color || '#6c757d',
                                    fontSize: '0.9rem',
                                    padding: '0.5rem 0.75rem'
                                  }}
                                >
                                  <i className="bi bi-tag me-1"></i>
                                  {tag.name}
                                  <span className="ms-2 badge bg-light text-dark">
                                    {tag.permissions?.length || 0}
                                  </span>
                                </Badge>
                              ))}
                            </div>
                          );
                        } else {
                          return <span className="text-muted">No tags assigned to actor</span>;
                        }
                      })()}
                    </Card.Body>
                  </Card>
                  
                  <Card>
                    <Card.Header className="bg-success text-white">
                      <strong><i className="bi bi-key me-2"></i>Effective Permissions ({selectedUserPermissions.length})</strong>
                    </Card.Header>
                    <Card.Body>
                      {selectedUserPermissions.length > 0 ? (
                        <div className="d-flex flex-wrap gap-2">
                          {selectedUserPermissions.map(perm => (
                            <Badge key={perm} bg="success" style={{fontSize: '0.85rem', padding: '0.4rem 0.6rem'}}>
                              <i className="bi bi-check-circle me-1"></i>
                              {perm.replace(/_/g, ' ')}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">
                          <i className="bi bi-exclamation-circle me-1"></i>
                          No permissions. Assign tags with permissions to the actor.
                        </span>
                      )}
                    </Card.Body>
                  </Card>
                  
                  <div className="d-flex justify-content-end mt-4">
                    <button className="um-btn um-btn-secondary" onClick={() => setShowPermissionsModal(false)}>
                      Close
                    </button>
                  </div>
                </>
              )}
            </Modal.Body>
          </Modal>

          {/* Grant Access Modal */}
          <Modal
            show={showGrantAccessModal}
            onHide={() => setShowGrantAccessModal(false)}
            backdrop="static"
            keyboard={false}
            centered
          >
            <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', color: '#fff' }}>
              <Modal.Title>
                <Key size={20} style={{ marginRight: 8 }} />
                Grant Login Access
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {selectedActor && (
                <>
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>
                      {`${selectedActor.first_name || ''} ${selectedActor.last_name || ''}`.trim() || selectedActor.name || 'Unknown'}
                    </div>
                    <div style={{ fontSize: 13, color: '#64748b' }}>{selectedActor.email || 'No email'}</div>
                  </div>

                  {formError && (
                    <Alert variant="danger" dismissible onClose={() => setFormError('')}>{formError}</Alert>
                  )}

                  <Form onSubmit={handleGrantAccessSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>Password</Form.Label>
                      <Form.Control
                        type="password"
                        name="password"
                        value={grantAccessForm.password}
                        onChange={handleGrantAccessFormChange}
                        autoComplete="new-password"
                        required
                        minLength={8}
                      />
                      <Form.Text className="text-muted">
                        At least 8 characters. User will be prompted to change on first login.
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Confirm Password</Form.Label>
                      <Form.Control
                        type="password"
                        name="confirmPassword"
                        value={grantAccessForm.confirmPassword}
                        onChange={handleGrantAccessFormChange}
                        autoComplete="new-password"
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        label="Grant Administrator privileges"
                        name="isAdmin"
                        checked={grantAccessForm.isAdmin}
                        onChange={handleGrantAccessFormChange}
                      />
                    </Form.Group>

                    <div className="d-flex justify-content-end gap-2">
                      <Button variant="secondary" onClick={() => setShowGrantAccessModal(false)}>
                        Cancel
                      </Button>
                      <Button variant="success" type="submit">
                        <Key size={16} style={{ marginRight: 6 }} />
                        Grant Access
                      </Button>
                    </div>
                  </Form>
                </>
              )}
            </Modal.Body>
          </Modal>
    </div>
  );
};

export default UserManagement;
